/* fc2c-buybox v4 — shared cross-tab selection state + variant-change persistence
   (round-4 redesign 2026-07-10, replaces v3's per-config-isolated selection). Markup
   pre-rendered by snippets/fc2c-buybox.liquid, placed BEFORE the native variant picker
   (title -> price -> ADD-ON PICKER -> native variant picker -> buy buttons). This script:
     1) toggles which SETUP/path config panel is visible (config chips, no fetching —
        every config's parts are already in the DOM),
     2) ROUND-4 TASK 2 (the big one): treats every kit_configs role-group as ONE SHARED
        SELECTION CHANNEL across every SETUP tab that lists it (keyed by role + that
        group's own option set since 2026-08-23, see channelKey(); valued by the
        selected part handle or 'none') — selecting a part in one tab shows it
        selected in every other tab that lists the same part (e.g. UDAPT in every path's
        HELMET MOUNT group), counts once toward the total/cart even from a hidden tab,
        and paints a small "+N" badge on a non-active chip when it holds a selection not
        visible in the current tab,
     3) ROUND-4 TASK 3: persists the full shared-selection-state object AND the active
        SETUP tab across a tube-serial (variant) change. The theme wholesale-replaces the
        [data-fc2cb] root on every variant:change, but this IIFE's own module scope
        (fc2cbState below) is never destroyed by that DOM replacement — see the boot()
        re-entry comment further down — so state simply survives; a sessionStorage
        mirror (keyed by product handle) is kept as a same-tab, cross-navigation
        fallback, memory always wins first,
     4) shows/hides groups per visible_if (configurator products only, unrelated to the
        SETUP/role-sharing system — PVS-14's authored steps have no SETUP tabs),
     5) computes the live add-on total (UNION of every shared-role selection, regardless
        of which tab is currently visible) and morphs the NATIVE price element's
        displayed amount (device + add-ons), with a small green "You save €X" note
        underneath — restores the native price when nothing is selected,
     6) toggles the native Buy with Shop / dynamic checkout button: visible when no
        add-ons are selected, hidden the moment any add-on is selected,
     7) collapses the native variant-picker option list on >8-variant gated products,
        only when it actually overflows 2 rows (hides "Show all" entirely otherwise),
     8) intercepts the native Add to cart click ONLY when at least one add-on is
        selected, combining it into a single multi-item /cart/add.js call (the UNION,
        deduped by role/handle so a shared part is added exactly once) and refreshing
        the cart drawer.
   The theme replaces the price / variant-picker / buy-buttons blocks wholesale on every
   variant:change (Impact's <product-rerender allow-partial-rerender> patches by
   data-block-type via element.replaceWith(), swapping in fresh SERVER-RENDERED markup --
   first chip active, every row unchecked -- before any of our JS has had a chance to run),
   so this script re-locates every element it touches on each variant:change instead of
   caching references, and re-runs boot() so a freshly inserted [data-fc2cb] root gets its
   listeners re-attached AND the persisted state re-applied (restoreState()).
   ROUND-6 FIX (2026-07-11, owner-reported flicker bug): the re-boot used to be triggered
   ONLY from the theme's own 'variant:change' CustomEvent, a SEPARATE event Impact dispatches
   after the swap. Depending on that second event left a real window where the swapped-in
   default-state markup could paint before our restore ran. Fixed by watching the swap
   itself with a MutationObserver on the stable ancestor that survives it (see
   ensureObserver()/findObserverTarget() below) -- its callback is a microtask that runs
   after the DOM mutation but before the browser's next paint, so restoreState() now runs
   off the mutation directly instead of waiting on a theme event. The 'variant:change'
   listener stays wired as an idempotent fallback (boot()'s own dataset.init guard makes a
   second call from either path a no-op, whichever one wins the race).
   ROUND-7 FIX (2026-07-11, owner-reported SETUP-chip flicker on non-first tabs): round-6
   made the restore run pre-paint at the DOM/attribute level (verified true), but the SETUP
   chip's "active" look still visibly cross-faded from the wrong chip to the right one for
   ~120ms. Cause: boot() called restoreState() at the very END, AFTER the variant-picker
   de-bulking block's getBoundingClientRect() calls (measureRows/applyCollapse/applyAll).
   getBoundingClientRect() forces a synchronous style/layout flush of the WHOLE document,
   which "observed" and locked in the freshly-swapped DEFAULT chip styling (chip 0 active)
   as a real computed-style checkpoint the CSS engine had actually observed -- even
   though this never reached an actual painted frame. restoreState()'s later class
   change (chip N active) then had TWO distinct observed styles to interpolate
   between, so the CSS transition on .fc2cb__setup-chip (background-
   color/border-color .12s) genuinely started and animated from the wrong chip to the
   right one over ~120ms: a real, visible cross-fade, not a paint-timing bug. Option-row
   groups never showed this because their dominant "checked" cue (box-shadow) carries no
   transition, so it always snapped instantly regardless of the same forced-layout pattern
   underneath -- masked, not fixed, by round 6. Fix: restoreState() is now called
   immediately after its own definition, before ANY getBoundingClientRect() read happens in
   that boot() pass, so no default/intermediate style is ever observed for a transition to
   animate from. Pure call-order change, no CSS touched. See restoreState()'s call site.
   ROUND-8 FIX (2026-07-11, owner STILL saw the SETUP-chip flicker after round 7, on a
   non-first tab, on every tube-serial change): round 7's call-order fix was verified
   correct at the DOM/attribute level and even pre-empted OUR OWN getBoundingClientRect
   call, yet the visible cross-fade persisted. Root cause this round is OUTSIDE this
   file: Impact's own <product-rerender>.onRerender_fn (assets/theme.js, pulled fresh
   and traced this round) replaces MULTIPLE [data-block-type] blocks -- sku, badges,
   price, payment-terms, variant-picker (OUR root lives here), quantity-selector,
   volume-pricing, inventory, buy-buttons, pickup-availability, liquid -- in ONE
   synchronous loop, all inside a single dispatchEvent('product:rerender') call, no
   await/rAF between any of them (confirmed by reading the loop body directly: a plain
   Array.forEach calling element.replaceWith(matchingBlock) per block type). Our
   MutationObserver callback is a MICROTASK, so it only fires once, after the ENTIRE
   loop finishes -- not after our own [data-fc2cb] root's specific replaceWith. That
   means any LATER block type in the loop (quantity-selector, buy-buttons, etc. --
   which run AFTER variant-picker in Impact's own blockTypes array) gets to run its own
   connectedCallback/attributeChangedCallback logic, fully synchronously, BEFORE our
   observer's microtask ever runs -- and if any of those does so much as one
   getBoundingClientRect/getComputedStyle/offsetWidth read anywhere in the document (a
   forced synchronous layout flush is document-wide, not scoped to that element's own
   subtree), it "observes" and locks in our just-swapped DEFAULT chip styling as a real
   checkpoint the CSS engine has genuinely seen -- the EXACT round-7 mechanism, just
   triggered by code we don't own and can't reorder, one level up the call stack from
   anything restoreState()'s own call-order can fix. No further chase of Impact's
   internals was pursued past confirming this mechanism is architecturally possible
   (verified: the loop is synchronous and multi-block; NOT verified line-by-line which
   specific later block's internals does the forced read, since that's Impact's code
   and can change under us on any theme update -- chasing it further would only buy
   another round-7-style game of whack-a-mole against code this project doesn't
   control). Fix: belt-and-suspenders, immune to WHO forces the flush. (1) A transition-
   kill class (fc2cb-no-anim) goes on the root the instant before restoreState() touches
   anything, forcing transition:none on the exact two properties round 7 identified
   (.fc2cb__setup-chip background-color/border-color, .fc2cb__opt border-color) for one
   guaranteed-painted frame (double rAF) with a 300ms timeout fallback for throttled/
   backgrounded tabs -- this makes the cross-fade structurally impossible regardless of
   how many checkpoints the CSS engine observes in between, since there is nothing to
   animate between when transitions are off. (2) An idempotent re-assert pass (rAF + 50ms
   timeout, both cheap no-ops if nothing changed) re-applies restoreState() shortly
   after, in case some later swap this session's instrumentation didn't catch re-wrote
   the DOM back to defaults after our first restore. See suppressTransitions() and
   scheduleReassert() below, and their call sites right after restoreState()'s own call.
   Any failure anywhere logs '[fc2cb]' and leaves the native page fully functional. */
(function () {
  'use strict';

  /* ROUND-3 owner bug (blocker): live-investigated extensively (multiple products/configs,
     synthetic + real clicks, price-calc + DOM-mutation observation) -- could NOT reproduce
     cross-group deselection with the deployed code; selectOption() has always scoped its
     aria-checked clear correctly. One genuine defect WAS found while investigating: every
     wholesale re-render (theme's variant:change patch) re-runs boot() on the fresh root and
     re-registers the document-level variant:change / native-change / ATC-click(capture)
     listeners WITHOUT ever removing the previous set. Hardened: exactly one active set of
     document-level listeners at a time. */
  var fc2cbCleanup = null;

  /* ===== ROUND-4 TASK 2 + 3: module-level shared state =====
     Lives OUTSIDE boot() so it survives every boot() re-entry triggered by a wholesale
     variant:change DOM replacement (the script tag itself is never re-executed by that
     replacement — Impact patches via innerHTML, which does not re-run inserted <script>
     tags — only the document-level `variant:change` listener registered at the ORIGINAL
     page-load boot() picks the change up and calls boot() again within the SAME module
     scope). configIndex = the active SETUP tab (0 if none).
     sel = { channelKey: handle|'none' } where channelKey is role + the group's own option
     set, shared across every config/tab that carries a group with that same shape. */
  var fc2cbState = { configIndex: 0, sel: {} };
  var fc2cbEverBooted = false;

  /* ===== ROUND-6: MutationObserver-driven synchronous restore (flicker fix, 2026-07-11) =====
     fc2cbObserver is created exactly ONCE per page load (module scope, same lifetime
     guarantee as fc2cbState above) and left connected for the rest of the page's life --
     the target it watches (see findObserverTarget) is never itself destroyed by a variant
     swap, so one long-lived observer covers every future swap too. fc2cbIsRestoring guards
     against the observer re-entering on mutations that boot()/restoreState() itself makes
     (aria-checked / hidden / price-text / badge writes are all childList-or-attribute
     mutations inside the very subtree being observed). */
  var fc2cbObserver = null;
  var fc2cbIsRestoring = false;

  /* Walk up from the current [data-fc2cb] root to find the nearest ancestor that Impact's
     <product-rerender allow-partial-rerender> element (js/common/product/product-rerender.js)
     does NOT replace on a same-product variant change: it only calls
     element.replaceWith(matchingBlock) on the specific [data-block-type] children matched
     against the fresh server fragment (price / variant-picker / buy-buttons / ...) --
     <product-rerender> itself is left in place (only a genuine product-to-product swap
     hits its own `this.replaceWith(matchingElement)` branch, which is not our case: tube
     serials are variants of the SAME product). That makes <product-rerender> the exact
     "stable ancestor of the picker" the fix needs -- observing it (not the [data-fc2cb]
     root, which IS one of the replaced nodes) lets us see the replacement happen instead
     of being destroyed by it.
     Fallback chain if the tag isn't present for some reason (defensive only -- every gated
     product page renders inside one): the .product-info <safe-sticky> wrapper (also never
     replaced for a variant change, one level further out), then <main>, then <body>. A
     "too high" target only costs the callback an extra cheap querySelector on unrelated
     mutations elsewhere on the page (header, cart drawer, etc.) -- never a correctness
     issue, since the callback below no-ops immediately unless it finds a genuinely
     uninitialized [data-fc2cb] root. */
  function findObserverTarget(fromRoot) {
    var el = fromRoot;
    while (el && el !== document.body) {
      el = el.parentElement;
      if (el && el.tagName === 'PRODUCT-RERENDER') return el;
    }
    return (fromRoot && fromRoot.closest && fromRoot.closest('.product-info'))
      || document.querySelector('main')
      || document.body;
  }

  /* Sets up the long-lived observer exactly once. Safe to call from every boot() -- the
     fc2cbObserver null-check makes every call after the first a no-op. childList+subtree
     catches element.replaceWith() anywhere under the target, which is how Impact performs
     the swap (see findObserverTarget's doc comment). */
  function ensureObserver(root) {
    if (fc2cbObserver) return;
    var target = findObserverTarget(root);
    if (!target) return;
    fc2cbObserver = new MutationObserver(function () {
      if (fc2cbIsRestoring) return;
      var freshRoot = document.querySelector('[data-fc2cb]');
      // Nothing to do if there's no picker on the page right now, or if it has already
      // been booted (either this same callback got here first on an earlier mutation
      // record in the batch, or the 'variant:change' fallback path won the race -- either
      // way boot()'s dataset.init guard already reflects the true state).
      if (!freshRoot || freshRoot.dataset.init === '1') return;
      fc2cbIsRestoring = true;
      try {
        // Synchronous, no setTimeout/rAF/await: this callback IS a microtask that runs
        // after the replaceWith() mutation but before the browser's next paint, so
        // boot() -> restoreState() finishing here means the FIRST frame ever painted for
        // the fresh root already reflects the restored tab + selections, not the
        // server-rendered default.
        boot();
      } finally {
        fc2cbIsRestoring = false;
      }
    });
    fc2cbObserver.observe(target, { childList: true, subtree: true });
  }

  function sessionKey(productHandle) {
    // v2 key (2026-08-23): the channel key changed shape (role -> role::optionset), so a
    // v1 payload from an older tab must not be hydrated back into the new state object.
    return 'fc2cb:v2:' + productHandle;
  }
  function persistState(productHandle) {
    try {
      if (!productHandle || !window.sessionStorage) return;
      sessionStorage.setItem(sessionKey(productHandle), JSON.stringify(fc2cbState));
    } catch (e) { /* storage unavailable/full -- memory state stays authoritative */ }
  }
  function hydrateFromSession(productHandle) {
    try {
      if (!productHandle || !window.sessionStorage) return;
      var raw = sessionStorage.getItem(sessionKey(productHandle));
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        fc2cbState.configIndex = parseInt(parsed.configIndex, 10) || 0;
        fc2cbState.sel = (parsed.sel && typeof parsed.sel === 'object') ? parsed.sel : {};
      }
    } catch (e) { /* ignore -- fresh state (Task 1 default: everything unselected) */ }
  }

  /* Shopify money formatting does NOT follow Intl/locale rules -- it follows the shop's
     own configured money_format string (Settings > General), independent of document
     language. Reading Intl.NumberFormat(document.documentElement.lang) produced US-style
     grouping (4,940.00) on this EU-formatted store (native price renders 4.700,00 EUR),
     a real mismatch once this function drives the PRIMARY price display, not just a
     secondary line. Fix: replicate Shopify's own {{amount}} token substitution using the
     theme's own exposed moneyFormat + currencyCodeEnabled settings. */
  function groupThousands(intStr, sep) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }
  function formatAmount(cents, style) {
    var value = Math.abs(cents) / 100;
    var negative = cents < 0;
    var sign = negative ? '-' : '';
    if (style === 'no_decimals') {
      return sign + groupThousands(Math.round(value).toString(), ',');
    }
    if (style === 'comma_separator') {
      var parts = value.toFixed(2).split('.');
      return sign + groupThousands(parts[0], '.') + ',' + parts[1];
    }
    if (style === 'no_decimals_comma_separator') {
      return sign + groupThousands(Math.round(value).toString(), '.');
    }
    var partsDefault = value.toFixed(2).split('.');
    return sign + groupThousands(partsDefault[0], ',') + '.' + partsDefault[1];
  }
  function money(cents) {
    try {
      var settings = (window.themeVariables && window.themeVariables.settings) || {};
      var fmt = settings.moneyFormat || '€{{amount}}';
      var out = fmt.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, function (_, token) {
        switch (token) {
          case 'amount_no_decimals': return formatAmount(cents, 'no_decimals');
          case 'amount_with_comma_separator': return formatAmount(cents, 'comma_separator');
          case 'amount_no_decimals_with_comma_separator': return formatAmount(cents, 'no_decimals_comma_separator');
          default: return formatAmount(cents, 'amount');
        }
      });
      if (settings.currencyCodeEnabled) {
        var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
        if (cur) out += ' ' + cur;
      }
      return out;
    } catch (e) {
      try {
        var cur2 = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
        return new Intl.NumberFormat('en', { style: 'currency', currency: cur2 }).format(cents / 100);
      } catch (e2) { return '€' + (cents / 100).toFixed(2); }
    }
  }

  function scopeRoot() {
    return document.querySelector('.fc2cb-on');
  }

  /* ===== native price morph ===== */
  function getSaleEl() {
    var scope = scopeRoot();
    if (!scope) return null;
    return scope.querySelector('.product-info__price sale-price');
  }
  function ensurePriceSpan(saleEl) {
    var span = saleEl.querySelector('[data-fc2cb-pricenum]');
    if (span) return span;
    var srOnly = saleEl.querySelector('.sr-only');
    var toMove = [];
    var node = srOnly ? srOnly.nextSibling : saleEl.firstChild;
    while (node) { toMove.push(node); node = node.nextSibling; }
    span = document.createElement('span');
    span.setAttribute('data-fc2cb-pricenum', '1');
    toMove.forEach(function (n) { span.appendChild(n); });
    saleEl.appendChild(span);
    span.dataset.fc2cbOriginal = span.textContent;
    return span;
  }
  function updatePriceDisplay(currentVariantPrice, addonNow, addonSave, anyAddon) {
    try {
      var saleEl = getSaleEl();
      if (!saleEl) return;
      var span = ensurePriceSpan(saleEl);
      var priceContainer = saleEl.closest('.product-info__price');
      /* fc 2026-08-12: for a logged-in TAG dealer the host price rendered by
         snippets/product-info.liquid is their own dealer price, but data-fc2cb-prices (and
         the variant:change payload) still carry RETAIL -- so composing host + add-on from
         those put a retail host back on the page the moment an add-on was ticked. The base
         is taken from the price node itself, which is the same node this function writes to
         and is re-rendered by the server on every variant change, so it can never be one
         variant behind. Absent for retail, logged-out and company logins: unchanged. */
      if (priceContainer && priceContainer.hasAttribute('data-fc-dealer-base')) {
        var fcDealerBase = parseInt(priceContainer.getAttribute('data-fc-dealer-base'), 10);
        if (fcDealerBase > 0) currentVariantPrice = fcDealerBase;
      }
      var ratingWithText = priceContainer ? priceContainer.querySelector('.rating-with-text') : null;
      var noteEl = priceContainer ? priceContainer.querySelector('[data-fc2cb-savenote]') : null;
      if (anyAddon) {
        span.textContent = money(currentVariantPrice + addonNow);
        if (!noteEl && priceContainer) {
          noteEl = document.createElement('p');
          noteEl.setAttribute('data-fc2cb-savenote', '1');
          noteEl.className = 'fc2cb-price-save';
          if (ratingWithText && ratingWithText.parentNode === priceContainer) {
            ratingWithText.insertAdjacentElement('afterend', noteEl);
          } else {
            priceContainer.appendChild(noteEl);
          }
        }
        if (noteEl) {
          if (addonSave > 0) {
            noteEl.hidden = false;
            noteEl.textContent = 'You save ' + money(addonSave) + ' with add-ons';
          } else {
            noteEl.hidden = true;
            noteEl.textContent = '';
          }
        }
      } else {
        span.textContent = span.dataset.fc2cbOriginal || span.textContent;
        if (noteEl) { noteEl.hidden = true; noteEl.textContent = ''; }
      }
    } catch (err) {
      if (window.console) console.warn('[fc2cb] failure (price morph):', err);
    }
  }

  /* ===== ROUND-8: transition suppression (chip-flicker belt-and-suspenders) =====
     See the ROUND-8 doc comment at the top of the file for the full mechanism this
     defends against. Adds NO_ANIM_CLASS to the current [data-fc2cb] root the instant
     before restoreState() touches any attribute/class, so the CSS engine has
     transition:none in effect for the two properties round 7 identified
     (.fc2cb__setup-chip background-color/border-color, .fc2cb__opt border-color -- see
     the matching rule in fc2c-buybox.liquid's <style>) across the entire restore. Held
     for one GUARANTEED-painted frame via a double requestAnimationFrame (the first rAF
     runs before the next paint but the class-add itself hasn't necessarily been
     "observed" yet by that same frame; the second rAF runs after a frame has already
     been painted WITH the class active, so removing it there can never let an
     intermediate/wrong style sneak into a transition), plus a 300ms setTimeout as a
     fallback removal path for throttled/backgrounded tabs where rAF may never fire (the
     same limitation rounds 6/7 flagged for their own instrumentation). release() is
     idempotent (guarded) and re-queries the DOM for whichever element currently holds
     the class rather than trusting the closed-over `root` reference, since that root
     may itself have been replaced again by the time either timer fires. */
  var NO_ANIM_CLASS = 'fc2cb-no-anim';
  function suppressTransitions(root) {
    if (!root) return;
    root.classList.add(NO_ANIM_CLASS);
    var released = false;
    function release() {
      if (released) return;
      released = true;
      root.classList.remove(NO_ANIM_CLASS);
      var freshRoot = document.querySelector('[data-fc2cb]');
      if (freshRoot && freshRoot !== root) freshRoot.classList.remove(NO_ANIM_CLASS);
    }
    if (window.requestAnimationFrame) {
      requestAnimationFrame(function () { requestAnimationFrame(release); });
    }
    setTimeout(release, 300);
  }

  /* ===== Buy with Shop / dynamic checkout toggle ===== */
  function updatePaymentButton(anyAddon) {
    try {
      var scope = scopeRoot();
      if (!scope) return;
      var btn = scope.querySelector('.shopify-payment-button');
      if (!btn) return;
      btn.style.display = anyAddon ? 'none' : '';
    } catch (err) {
      if (window.console) console.warn('[fc2cb] failure (payment button toggle):', err);
    }
  }

  function boot() {
    var root = document.querySelector('[data-fc2cb]');
    if (!root) return;
    // Set up the swap-watching observer off THIS root (whichever one happens to call
    // boot() first -- page load, the observer's own callback, or the variant:change
    // fallback). No-ops after the first successful call; see ensureObserver() above.
    ensureObserver(root);
    if (root.dataset.init === '1') return;
    root.dataset.init = '1';

    try {
      // remove the PREVIOUS boot()'s document-level listeners (if any) before adding a
      // new set -- prevents unbounded accumulation across repeated variant:change
      // wholesale re-renders.
      if (fc2cbCleanup) { fc2cbCleanup(); fc2cbCleanup = null; }

      var formId = root.dataset.formId;
      var productHandle = root.dataset.productHandle || '';

      // ROUND-4 TASK 3: hydrate from sessionStorage ONLY on the very first boot() this
      // script instance ever runs (a genuine fresh page load). Every later boot() call
      // (a variant:change reboot) is memory-first: fc2cbState already holds the live,
      // in-session-accurate selections, so re-reading storage would be redundant at
      // best -- never destructive, since persistState() keeps storage in lockstep on
      // every recalc(), but skipped anyway to keep the memory path the single source of
      // truth after the first boot, per the "memory-first" instruction.
      if (!fc2cbEverBooted) {
        hydrateFromSession(productHandle);
        fc2cbEverBooted = true;
      }

      var stepGroups = Array.prototype.slice.call(root.querySelectorAll('[data-fc2cb-step]'));
      // configurator-mode groups (PVS-14 authored steps, visible_if): these never carry
      // data-fc2cb-role (that attribute only exists on kit_configs-derived groups), so
      // this filter cleanly separates the two systems.
      var plainStepGroups = stepGroups.filter(function (g) { return !g.hasAttribute('data-fc2cb-role'); });

      /* BUGFIX 2026-08-23 (QDF/QDB): the shared channel used to be keyed by ROLE ALONE,
         so two SETUP tabs that use the same role for DIFFERENT parts (QDF/QDB: every
         tab's ADAPTER group holds a different device-specific QDA) collapsed into one
         state slot -- picking a QDA in one tab blanked every other tab's group including
         its own "No adapter" row, and only one QDA could ever reach the cart. The key is
         now role + the group's OWN option set, so groups offering the SAME options still
         share one channel (UDAPT helmet mount across every FC-M path -- the behaviour
         round-4 task 2 was built for) and groups offering different options each get
         their own channel. */
      function channelKey(group) {
        var handles = Array.prototype.map.call(
          group.querySelectorAll('.fc2cb__opt'),
          function (o) { return o.getAttribute('data-fc2cb-opt'); }
        ).filter(function (h) { return h && h !== 'none'; }).sort().join('|');
        return group.getAttribute('data-fc2cb-role') + '::' + handles;
      }

      // ROUND-4 TASK 2: channel -> [group elements] across EVERY config/tab that lists
      // that role with that same option set. This is the shared-selection channel map.
      var roleGroupMap = {};
      Array.prototype.forEach.call(root.querySelectorAll('[data-fc2cb-role]'), function (g) {
        var r = channelKey(g);
        if (!roleGroupMap[r]) roleGroupMap[r] = [];
        roleGroupMap[r].push(g);
      });

      var statusEl = root.querySelector('[data-fc2cb-status]');
      var pricesEl = root.querySelector('[data-fc2cb-prices]');
      var prices = {};
      try { prices = JSON.parse((pricesEl && pricesEl.textContent) || '{}'); } catch (e) { prices = {}; }

      var currentVariantId = root.dataset.initialVariant || '';
      var currentVariantPrice = 0;
      function syncVariantPrice() {
        if (prices.hasOwnProperty(currentVariantId)) currentVariantPrice = parseInt(prices[currentVariantId], 10) || 0;
      }
      syncVariantPrice();

      // configurator-mode's own local (non-shared) selection map, keyed by step id --
      // unrelated to fc2cbState.sel, which is reserved for the kit_configs role system.
      var confSel = {};

      /* ===== SETUP / path chips (Task 2 — config switching, no fetching) ===== */
      var setupRow = root.querySelector('[data-fc2cb-setup]');
      var configPanels = Array.prototype.slice.call(root.querySelectorAll('[data-fc2cb-config]'));

      function findOptByRoleAndHandle(role, handle) {
        var groupsForRole = roleGroupMap[role] || [];
        for (var i = 0; i < groupsForRole.length; i++) {
          var opts = groupsForRole[i].querySelectorAll('.fc2cb__opt');
          for (var j = 0; j < opts.length; j++) {
            if (opts[j].getAttribute('data-fc2cb-opt') === handle) return opts[j];
          }
        }
        return null;
      }

      function applyRoleState(role) {
        var handle = fc2cbState.sel[role] || 'none';
        (roleGroupMap[role] || []).forEach(function (group) {
          Array.prototype.forEach.call(group.querySelectorAll('.fc2cb__opt'), function (opt) {
            var isMatch = opt.getAttribute('data-fc2cb-opt') === handle;
            opt.setAttribute('aria-checked', isMatch ? 'true' : 'false');
          });
        });
      }
      function applyAllRoleState() {
        Object.keys(roleGroupMap).forEach(applyRoleState);
      }

      /* ===== ROUND-4 TASK 2: honest cross-tab badge ===== */
      function updateSetupBadges() {
        if (!setupRow) return;
        var activePanel = null;
        for (var i = 0; i < configPanels.length; i++) {
          if (!configPanels[i].hidden) { activePanel = configPanels[i]; break; }
        }
        var activeRoles = activePanel
          ? Array.prototype.map.call(activePanel.querySelectorAll('[data-fc2cb-role]'), function (g) { return channelKey(g); })
          : [];
        configPanels.forEach(function (panel) {
          var idx = panel.getAttribute('data-fc2cb-config');
          var chip = setupRow.querySelector('[data-fc2cb-setup-chip="' + idx + '"]');
          if (!chip) return;
          var badge = chip.querySelector('[data-fc2cb-chip-badge]');
          if (!badge) return;
          if (panel === activePanel) { badge.hidden = true; badge.textContent = ''; return; }
          var roles = Array.prototype.map.call(panel.querySelectorAll('[data-fc2cb-role]'), function (g) { return channelKey(g); });
          var count = 0;
          roles.forEach(function (r) {
            if (fc2cbState.sel[r] && fc2cbState.sel[r] !== 'none' && activeRoles.indexOf(r) === -1) count++;
          });
          if (count > 0) { badge.hidden = false; badge.textContent = '+' + count; }
          else { badge.hidden = true; badge.textContent = ''; }
        });
      }

      function selectConfig(index) {
        index = String(parseInt(index, 10) || 0);
        fc2cbState.configIndex = parseInt(index, 10);
        configPanels.forEach(function (panel) {
          panel.hidden = panel.getAttribute('data-fc2cb-config') !== index;
        });
        if (setupRow) {
          Array.prototype.forEach.call(setupRow.querySelectorAll('[data-fc2cb-setup-chip]'), function (chip) {
            var isSel = chip.getAttribute('data-fc2cb-setup-chip') === index;
            chip.classList.toggle('is-selected', isSel);
            chip.setAttribute('aria-checked', isSel ? 'true' : 'false');
          });
        }
        recalc();
      }
      if (setupRow) {
        setupRow.addEventListener('click', function (e) {
          var chip = e.target.closest ? e.target.closest('[data-fc2cb-setup-chip]') : null;
          if (!chip || !setupRow.contains(chip)) return;
          selectConfig(chip.getAttribute('data-fc2cb-setup-chip'));
        });
      }

      function visible(group) {
        var configWrap = group.closest('[data-fc2cb-config]');
        if (configWrap && configWrap.hidden) return false;
        var rule = group.getAttribute('data-visible-if');
        if (!rule) return true;
        try {
          var r = JSON.parse(rule);
          return Object.keys(r).every(function (k) {
            var a = Array.isArray(r[k]) ? r[k] : [r[k]];
            return a.indexOf(confSel[k]) >= 0;
          });
        } catch (e) { return true; }
      }

      function recalc() {
        var addonNow = 0;
        var addonSave = 0;
        var anyAddon = false;

        // --- configurator-mode groups (visible_if flow, own local confSel, no cross-tab
        // sharing since this mode has no SETUP tabs at all) ---
        plainStepGroups.forEach(function (group) {
          var on = visible(group);
          group.hidden = !on;
          var id = group.getAttribute('data-fc2cb-step');
          if (!on) { delete confSel[id]; return; }
          var chosen = group.querySelector('.fc2cb__opt[aria-checked="true"]');
          if (!chosen) return;
          confSel[id] = chosen.getAttribute('data-fc2cb-opt');
          var isNone = chosen.getAttribute('data-none') === '1';
          if (!isNone) {
            var was = parseInt(chosen.getAttribute('data-was') || '0', 10);
            var now = parseInt(chosen.getAttribute('data-now') || '0', 10);
            var rateKind = chosen.getAttribute('data-rate-kind');
            addonNow += now;
            if (rateKind !== 'nv') addonSave += Math.max(0, was - now);
            anyAddon = true;
          }
        });

        // --- ROUND-4 TASK 2: kit_configs shared-role groups. UNION across every
        // tab/config, including hidden ones (owner's explicit wish: "maybe they want
        // one piece from one setup and another from other"). Each role contributes
        // exactly ONCE regardless of how many config subtrees render it. ---
        Object.keys(roleGroupMap).forEach(function (role) {
          var handle = fc2cbState.sel[role];
          if (!handle || handle === 'none') return;
          var opt = findOptByRoleAndHandle(role, handle);
          if (!opt) return;
          var was = parseInt(opt.getAttribute('data-was') || '0', 10);
          var now = parseInt(opt.getAttribute('data-now') || '0', 10);
          var rateKind = opt.getAttribute('data-rate-kind');
          addonNow += now;
          if (rateKind !== 'nv') addonSave += Math.max(0, was - now);
          anyAddon = true;
        });

        updatePriceDisplay(currentVariantPrice, addonNow, addonSave, anyAddon);
        updatePaymentButton(anyAddon);
        updateSetupBadges();
        persistState(productHandle);

        return anyAddon;
      }

      function selectOption(opt) {
        if (opt.getAttribute('data-avail') === '0') return;
        var roleGroup = opt.closest('[data-fc2cb-role]');
        if (roleGroup) {
          // ROUND-4 TASK 2: shared channel -- write the ONE state object, then sync
          // aria-checked on every group (every tab) that shares this role, not just
          // the group the click happened in.
          var role = channelKey(roleGroup);
          var handle = opt.getAttribute('data-fc2cb-opt');
          fc2cbState.sel[role] = handle;
          applyRoleState(role);
        } else {
          var group = opt.closest('[data-fc2cb-step]');
          if (!group) return;
          group.querySelectorAll('.fc2cb__opt').forEach(function (o) { o.setAttribute('aria-checked', 'false'); });
          opt.setAttribute('aria-checked', 'true');
        }
        recalc();
      }

      root.addEventListener('click', function (e) {
        var opt = e.target.closest ? e.target.closest('.fc2cb__opt') : null;
        if (!opt || !root.contains(opt)) return;
        selectOption(opt);
      });

      root.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        var opt = e.target.closest ? e.target.closest('.fc2cb__opt') : null;
        if (opt) { e.preventDefault(); selectOption(opt); }
      });

      /* ===== ROUND-4 TASK 3: restore active tab + full shared state after boot() =====
         Applies BEFORE the first recalc() so the very first paint after a fresh boot
         (page load OR post-variant-change reboot) already reflects persisted state --
         visually (checked marks + active tab + badges) and numerically (price/total,
         since recalc() reads fc2cbState.sel directly, not DOM). On a genuine first-ever
         boot with no prior session data, fc2cbState.sel is {} and configIndex is 0,
         which is exactly Task 1's "everything starts unselected, first tab active"
         default -- restoreState() is safe to always call unconditionally. */
      function restoreState() {
        applyAllRoleState();
        selectConfig(fc2cbState.configIndex || 0);
      }

      /* ROUND-8: idempotent re-assert pass, closed over THIS boot() call's root/state
         (needs applyAllRoleState/selectConfig from this same closure, so it lives here,
         not at module scope like suppressTransitions). Fires once via rAF (catches a
         same-frame overwrite) and once via a 50ms timeout (catches a slightly later one,
         e.g. a second block-type swap or a delayed script effect) -- both cheap no-ops
         when nothing changed since restoreState() already ran. Re-queries the live
         [data-fc2cb] root fresh on every fire rather than trusting a captured reference:
         if a DIFFERENT (later-swapped, not-yet-restored) root is now in the document,
         defers to boot() itself (which re-runs the whole init incl. restoreState() on
         the new root) instead of re-applying THIS closure's state to the wrong element;
         if the SAME root captured at this boot() call is still live, just re-applies --
         belt-and-suspenders for the case where the observer's microtask fired but a
         later, independent mutation still overwrote the DOM back to defaults for a
         frame. See the ROUND-8 doc comment at the top of the file. */
      function scheduleReassert(bootedRoot) {
        function pass() {
          var freshRoot = document.querySelector('[data-fc2cb]');
          if (!freshRoot) return;
          if (freshRoot.dataset.init !== '1') { boot(); return; }
          if (freshRoot === bootedRoot) {
            applyAllRoleState();
            selectConfig(fc2cbState.configIndex || 0);
          }
        }
        if (window.requestAnimationFrame) requestAnimationFrame(pass);
        setTimeout(pass, 50);
      }

      /* ROUND-7 FIX (2026-07-11, owner-reported SETUP-chip flicker): restoreState() is
         called HERE, immediately after its own definition -- NOT at the end of boot() as
         in round-6 -- so it runs BEFORE the variant-picker de-bulking block below ever
         calls getBoundingClientRect() (see measureRows()/applyCollapse()/applyAll()).
         Root cause: getBoundingClientRect() forces a SYNCHRONOUS style/layout flush of
         the whole document. When that flush ran BEFORE restoreState() (round-6 order),
         it "locked in" the freshly-swapped, still-DEFAULT chip styling (chip 0 active) as
         a real computed-style checkpoint the CSS engine had actually observed -- even
         though this never reached an actual painted frame. restoreState()'s later class
         change (chip N active) then had TWO distinct observed styles to interpolate
         between, so the CSS transition on .fc2cb__setup-chip (background-color/
         border-color .12s) genuinely started and animated from the wrong chip to the
         right one over ~120ms: a real, visible cross-fade, not a paint-timing bug. This
         explains why SETUP chips flickered but the option-row groups did not even under
         the SAME synchronous restore call: .fc2cb__opt's dominant "checked" cue is
         box-shadow, which carries NO transition (only border-color does), so it always
         snapped instantly regardless of any earlier forced layout -- the bug was masked
         there by coincidence, not fixed. Calling restoreState() before any
         getBoundingClientRect() read in this boot() pass means the DOM never exposes an
         intermediate/default style to a forced layout in the first place, so there is no
         "before" checkpoint left for a transition to animate from -- chips now snap
         instantly like the groups always did. No CSS/transition rules were changed;
         this is a pure call-order fix. */
      /* ROUND-8: suppressTransitions() goes on BEFORE restoreState() touches any
         attribute/class (belt), scheduleReassert() goes on AFTER (suspenders). See the
         ROUND-8 doc comment at the top of the file for the full mechanism. Neither
         changes what restoreState() itself does -- both wrap it defensively. */
      suppressTransitions(root);
      restoreState();
      scheduleReassert(root);

      /* ===== current variant tracking + re-attach on theme re-render (Task 3) =====
         Impact's <product-rerender allow-partial-rerender> patches the price / variant-
         picker / buy-buttons blocks with fresh server HTML on every variant:change, which
         wholesale-replaces this picker's own [data-fc2cb] root too (it lives inside the
         same variant_picker block). Re-running boot() is idempotent (guarded by
         dataset.init) and re-attaches listeners to a freshly inserted root; re-locating
         the price/payment-button elements happens every recalc() regardless. The fresh
         root's own boot() call ends with restoreState(), which re-applies the SAME
         module-level fc2cbState (active tab + every shared selection) to the brand-new
         DOM -- nothing resets to defaults on a variant change.
         ROUND-6: this listener is now a FALLBACK, not the primary re-boot trigger. The
         primary trigger is ensureObserver()'s MutationObserver, which reacts to the
         replaceWith() swap itself (a microtask, guaranteed before the next paint) instead
         of waiting for this separate 'variant:change' CustomEvent. Whichever path's boot()
         call reaches the fresh root first wins (both are synchronous, no setTimeout/rAF in
         either) and sets dataset.init='1'; the other path's own boot() call then no-ops on
         that same guard, so this listener staying wired is redundant-but-harmless, kept as
         a fallback in case a future theme change ever fires variant:change without the DOM
         mutation the observer expects (e.g. a price-only refresh). */
      function onVariantChange(e) {
        try {
          if (!e.detail || e.detail.formId !== formId || !e.detail.variant) return;
          currentVariantId = String(e.detail.variant.id);
          if (typeof e.detail.variant.price === 'number') currentVariantPrice = e.detail.variant.price;
          else syncVariantPrice();
          boot();
          var freshRoot = document.querySelector('[data-fc2cb]');
          if (freshRoot && freshRoot !== root) return; // fresh root's own boot() already ran restoreState()/recalc()
          recalc();
        } catch (err) { if (window.console) console.warn('[fc2cb]', err); }
      }
      document.addEventListener('variant:change', onVariantChange);

      function onNativeChange(e) {
        try {
          var t = e.target;
          if (!t || !t.matches || !t.matches('input[name="id"]')) return;
          var form = t.closest('form');
          if (!form || form.getAttribute('id') !== formId) return;
          currentVariantId = String(t.value);
          syncVariantPrice();
          recalc();
        } catch (err) { if (window.console) console.warn('[fc2cb]', err); }
      }
      document.addEventListener('change', onNativeChange);

      /* ===== variant-picker de-bulking (>8 variants only; hide "Show all" entirely
         unless the list genuinely overflows 2 rows — markup/CSS untouched, class +
         inline max-height only). Row height/gap is measured LIVE (not a hardcoded px
         guess) so exactly 2 full chip rows show, never a clipped 3rd. Recomputed on
         resize since chips-per-row (and whether it even overflows) can change
         independently on desktop and mobile.
         BUG FIX 2026-07-10: the toggle button now renders in product-info.liquid,
         immediately after the native picker, as a SIBLING of the .fc2cb root (not a
         descendant of it) -- see fc2c-buybox.liquid's doc comment for why. Query it
         via document, not root, or this lookup would always return null. Still exactly
         one instance in the DOM (confirmed live: product-info/fc2c-buybox/the native
         picker are each rendered once per page, no separate mobile/desktop copies). ===== */
      var showAllBtn = document.querySelector('[data-fc2cb-show-all]');
      if (showAllBtn) {
        var lists = Array.prototype.slice.call(document.querySelectorAll('.product-info__variant-picker .variant-picker__option-values'));
        if (lists.length) {
          function measureRows(list) {
            var items = Array.prototype.filter.call(list.children, function (el) {
              // display:none swatches (sold serials, hidden by theme CSS since
              // 2026-08-23) measure as rect(0,0) and would fake a row at top 0.
              if (el.offsetParent === null) return false;
              return el.tagName === 'LABEL' || el.offsetHeight > 4;
            });
            if (!items.length) return [];
            var rowBottoms = [];
            var lastTop = null;
            items.forEach(function (el) {
              var r = el.getBoundingClientRect();
              var top = Math.round(r.top);
              if (lastTop === null || Math.abs(top - lastTop) > 2) {
                rowBottoms.push(r.bottom);
                lastTop = top;
              } else if (rowBottoms.length) {
                rowBottoms[rowBottoms.length - 1] = Math.max(rowBottoms[rowBottoms.length - 1], r.bottom);
              }
            });
            return rowBottoms;
          }
          function applyCollapse(list) {
            var containerTop = list.getBoundingClientRect().top;
            var rowBottoms = measureRows(list);
            if (rowBottoms.length > 2) {
              list.classList.add('fc2cb-vp-collapsed');
              list.style.maxHeight = Math.ceil(rowBottoms[1] - containerTop) + 'px';
              showAllBtn.hidden = false;
            } else {
              list.classList.remove('fc2cb-vp-collapsed');
              list.style.maxHeight = '';
              showAllBtn.hidden = true;
            }
          }
          function applyAll() { lists.forEach(applyCollapse); }
          applyAll();
          var resizeTimer = null;
          window.addEventListener('resize', function () {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(function () {
              // recheck row count independently at the new width (desktop/mobile can
              // cross the 2-row threshold in either direction as chips reflow)
              lists.forEach(applyCollapse);
            }, 150);
          });
          showAllBtn.addEventListener('click', function () {
            lists.forEach(function (list) { list.classList.remove('fc2cb-vp-collapsed'); list.style.maxHeight = ''; });
            showAllBtn.hidden = true;
          });
        } else {
          showAllBtn.hidden = true;
        }
      }

      /* ===== ATC intercept: only when at least one non-None add-on is selected ===== */
      function refreshCartDrawer() {
        return fetch('/?sections=cart-drawer', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (sections) {
            if (!sections || !sections['cart-drawer']) return;
            var oldDrawer = document.getElementById('cart-drawer');
            if (!oldDrawer) return;
            var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
            var newDrawer = doc.getElementById('cart-drawer') || doc.querySelector('cart-drawer');
            if (newDrawer) oldDrawer.innerHTML = newDrawer.innerHTML;
          });
      }
      function openCartDrawer() {
        var trigger = document.querySelector('a[aria-controls="cart-drawer"]');
        if (trigger) { trigger.click(); return; }
        var drawer = document.getElementById('cart-drawer');
        if (drawer && typeof drawer.show === 'function') drawer.show();
      }

      // QTY 2026-08-07: a part row may ask for more than one unit (data-qty, emitted by
      // fc2c-buybox.liquid from kit_configs part.qty). Absent or junk attribute = 1, so
      // every pre-existing row behaves exactly as before.
      function optQty(el) {
        var q = parseInt((el && el.getAttribute('data-qty')) || '1', 10);
        return q > 0 ? q : 1;
      }

      function buildItems() {
        var items = [];
        if (currentVariantId) items.push({ id: parseInt(currentVariantId, 10), quantity: 1 });

        // configurator-mode groups (own local flow, currently visible only)
        plainStepGroups.forEach(function (group) {
          if (group.hidden) return;
          var chosen = group.querySelector('.fc2cb__opt[aria-checked="true"]');
          if (!chosen || chosen.getAttribute('data-none') === '1') return;
          var chosenQty = optQty(chosen);
          (chosen.getAttribute('data-vids') || '').split(',').forEach(function (v) {
            v = v.trim();
            if (v) items.push({ id: parseInt(v, 10), quantity: chosenQty });
          });
        });

        // ROUND-4 TASK 2: kit_configs shared-role groups -- the UNION, added exactly
        // once per role regardless of how many tabs display the same part.
        Object.keys(roleGroupMap).forEach(function (role) {
          var handle = fc2cbState.sel[role];
          if (!handle || handle === 'none') return;
          var opt = findOptByRoleAndHandle(role, handle);
          if (!opt) return;
          var roleQty = optQty(opt);
          (opt.getAttribute('data-vids') || '').split(',').forEach(function (v) {
            v = v.trim();
            if (v) items.push({ id: parseInt(v, 10), quantity: roleQty });
          });
        });

        return items;
      }

      function handleAtcClick(e) {
        try {
          var btn = e.target.closest ? e.target.closest('.product-info__buy-buttons button[type="submit"]') : null;
          if (!btn) return;
          var form = btn.closest('form');
          // NOTE: do not read form.id here. Every Shopify product form ships a hidden
          // <input type="hidden" name="id" ...> for the selected variant, and per the HTML
          // spec forms have [OverrideBuiltins]: a named form control shadows same-named IDL
          // properties on the form element itself. So form.id resolves to that <input>
          // element (typeof "object"), NOT the form's id attribute string -- form.id !== formId
          // was ALWAYS false, silently killing the intercept. Read the attribute directly.
          if (!form || form.getAttribute('id') !== formId) return;
          if (!recalc()) return; // no add-ons selected: let the native flow run untouched


          e.preventDefault();
          e.stopImmediatePropagation();

          var items = buildItems();
          if (!items.length) return;

          btn.disabled = true;
          if (statusEl) statusEl.textContent = '';

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
          })
            .then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); })
            .then(function () { return Promise.all([refreshCartDrawer()]); })
            .then(function () {
              btn.disabled = false;
              openCartDrawer();
            })
            .catch(function (err) {
              btn.disabled = false;
              if (statusEl) statusEl.textContent = 'Could not add everything - please try again.';
              if (window.console) console.warn('[fc2cb] failure', err);
            });
        } catch (err) {
          if (window.console) console.warn('[fc2cb] failure', err);
        }
      }

      document.addEventListener('click', handleAtcClick, true);

      fc2cbCleanup = function () {
        document.removeEventListener('variant:change', onVariantChange);
        document.removeEventListener('change', onNativeChange);
        document.removeEventListener('click', handleAtcClick, true);
      };

      // ROUND-7: restoreState() now runs earlier -- see the call site right after its
      // own function definition above, before the variant-picker de-bulking block --
      // so it completes before this boot() pass ever forces a layout via
      // getBoundingClientRect(). See that call site's doc comment for the full why.
    } catch (err) {
      if (window.console) console.warn('[fc2cb] failure (boot):', err);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
